export const ID_TOKEN_STORAGE_KEY = "idToken";
export const ID_TOKEN_EXPIRY_STORAGE_KEY = "idTokenExpiryMs";
const LEGACY_SHARED_AUTH_COOKIE = "canary-auth";
const LEGACY_SHARED_COOKIE_DOMAIN = ".canaryai.com";

// This app used to both read and write a `canary-auth` cookie shared across
// .canaryai.com, carrying a live Firebase ID token in plain text. Products now
// hand their token over explicitly via SSO instead, so this only evicts any
// copy still sitting in a browser from a previous visit — it never reads or
// writes one again.
function evictLegacySharedAuthCookie() {
  if (typeof document === "undefined") return;
  try {
    document.cookie = `${LEGACY_SHARED_AUTH_COOKIE}=; path=/; domain=${LEGACY_SHARED_COOKIE_DOMAIN}; SameSite=Lax; max-age=0;`;
  } catch {
    // document.cookie might be unavailable
  }
}

// An expired token is dropped rather than returned: callers treated any stored
// token as "someone is signed in", so a weeks-old one left behind after the
// session ended kept re-triggering sign-in attempts on the login page.
export function getStoredIdToken() {
  if (typeof window === "undefined") return null;
  try {
    const stored = localStorage.getItem(ID_TOKEN_STORAGE_KEY);
    if (!stored) return null;
    const expiry = getTokenExpiryFromJwt(stored);
    if (expiry && expiry <= Date.now()) {
      localStorage.removeItem(ID_TOKEN_STORAGE_KEY);
      localStorage.removeItem(ID_TOKEN_EXPIRY_STORAGE_KEY);
      return null;
    }
    return stored;
  } catch {
    return null;
  }
}

export function getStoredTokenExpiryMs() {
  if (typeof window === "undefined") return null;
  const raw = localStorage.getItem(ID_TOKEN_EXPIRY_STORAGE_KEY);
  if (!raw) return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

// JWT payloads are base64url; atob only accepts standard base64.
const decodeJwtPayload = (payload) =>
  JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));

export function getTokenExpiryFromJwt(token) {
  if (!token) return null;
  try {
    const [, payload] = token.split(".");
    if (!payload) return null;
    const decoded = decodeJwtPayload(payload);
    if (!decoded?.exp) return null;
    return decoded.exp * 1000;
  } catch {
    return null;
  }
}

export function getEmailFromIdToken(token) {
  if (!token) return null;
  try {
    const [, payload] = token.split(".");
    if (!payload) return null;
    const decoded = decodeJwtPayload(payload);
    const email =
      decoded?.email ||
      decoded?.user_email ||
      decoded?.preferred_username ||
      decoded?.upn ||
      (Array.isArray(decoded?.firebase?.identities?.email)
        ? decoded.firebase.identities.email[0]
        : null);
    return typeof email === "string" ? email : null;
  } catch {
    return null;
  }
}

export function persistIdToken(token) {
  if (typeof window === "undefined") return;
  if (!token) return;
  localStorage.setItem(ID_TOKEN_STORAGE_KEY, token);
  const expiry = getTokenExpiryFromJwt(token);
  if (expiry) {
    localStorage.setItem(ID_TOKEN_EXPIRY_STORAGE_KEY, String(expiry));
  } else {
    localStorage.removeItem(ID_TOKEN_EXPIRY_STORAGE_KEY);
  }
  evictLegacySharedAuthCookie();
}

export function clearStoredIdToken() {
  if (typeof window === "undefined") return;
  localStorage.removeItem(ID_TOKEN_STORAGE_KEY);
  localStorage.removeItem(ID_TOKEN_EXPIRY_STORAGE_KEY);
  evictLegacySharedAuthCookie();
}
